import React from 'react';
import styled, { css } from 'styled-components';
import { Checkbox } from '@lmn/design-system';
import LabelNode from '@/app/common/filters/types/shared/LabelNode.tsx';
import { getMealPlanIcon } from '@/app/components/search/components/Filters/verticalFilters/Fields/getMealPlanField/getMealPlanField.tsx';
// import { fontSizeSmall } from '@/app/components/search/styles/font.ts';
// import { gray100, primary, primary50 } from '@/app/components/search/styles/colors.ts';
import type { CustomComponentProps } from '@/app/common/filters/types/shared/Custom/types.tsx';
import { focusRing, srOnly } from '@/app/components/search/styles/mixins.ts';

const MealPlanIcon = styled.span<{ selected?: boolean }>`
  width: 48px;
  height: 48px;
  min-height: 48px;
  border-radius: 24px;
  border: 1px solid ${(p) => p.theme.kallisto.color.border.static.borderNeutral};
  display: flex;
  align-items: center;
  justify-content: center;
  transition:
    color 0.3s ease-out,
    border-color 0.3s ease-out;
  margin-bottom: 4px !important;

  ${({ selected }) =>
    selected &&
    css`
      background-color: ${({ theme }) => theme.kallisto.color.background.interactive.backgroundPrimarySelected};
      color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
      border-color: ${({ theme }) => theme.kallisto.color.background.interactive.backgroundPrimarySelected};
    `}

  svg {
    width: 24px;
    height: 24px;
  }
`;

const MealPlanCheckbox = styled(Checkbox)`
  padding: 0;
  transition: color 0.3s ease-out;

  &:hover {
    background-color: transparent;

    ${MealPlanIcon} {
      color: ${({ theme }) => theme.kallisto.color.text.interactive.textPrimaryDefault};
      border-color: ${({ theme }) => theme.kallisto.color.border.interactive.borderPrimaryHover};
    }
  }

  button {
    ${srOnly};
  }

  &:has(button:focus-visible) ${MealPlanIcon} {
    ${focusRing};
  }

  > span {
    display: flex;
    align-items: center;
    flex-direction: column;
    justify-content: flex-start;
    text-align: center;
    background: transparent;
    margin: 0 !important;
    width: 100%;
    height: 100%;

    [class*='LabelText'] {
      display: flex;
      flex-direction: column;
      align-items: center;
    }

    span {
      text-align: center;
      line-height: 15px;
      font-size: ${({ theme }) => theme.kallisto.font.size['150']}; // 12px
      margin: 0;
    }
  }
`;

export const MealPlanOptionComponent = ({ option, onChange, name }: CustomComponentProps) => {
  const icon = getMealPlanIcon()[parseInt(option.value, 10)];

  return (
    <MealPlanCheckbox
      key={option.value}
      checkbox={{
        id: `${name}-${option.value}`,
        label: (
          <>
            <MealPlanIcon selected={option.selected}>{icon}</MealPlanIcon>
            <LabelNode option={option} isDisabled={!!option?.disabled} />
          </>
        ),
      }}
      onChange={() => onChange(option.value)}
      labelPosition="end"
      isChecked={option.selected}
      disabled={option.disabled}
      fullWidth={true}
      data-checked={option.selected}
    />
  );
};
